import { config } from 'config';
import { daysOfWeek } from 'shared/fixtures';
import orderBy from 'lodash/orderBy';
import { turnsActions } from 'core/Turns';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AgendaContainer,
    ButtonsContainer,
    Col,
    Container,
    DayLabel,
    DayRow,
    Row
} from './styled';
import {
    Checkbox,
    DateRangePicker,
    ExpandButton,
    FormGroup,
    TagList,
    TimeRangePicker
} from 'mol-ui';
import {
    ConfirmationModal,
    ErrorMessage,
    FormLayout
} from 'views/components';
import {
    DoctorPicker,
    OfficePicker
} from 'views/pickers';
import React, { useEffect, useState } from 'react';
import {
    overlapTime,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useQuery,
    useSelector
} from 'hooks';

const { blockedWeekDays } = config;

const AgendaRegistration = () => {

    const dispatch = useDispatch();
    const { id: agenda_id } = useParams();
    const { t } = useTranslation();
    const { professional } = useQuery();
    const {
        user: {
            restricted_access
        }
    } = useSelector('account');
    const {
        agenda: turnsAgenda,
        agendaError
    } = useSelector('turns');
    const createMode = !agenda_id;
    const editMode = !!agenda_id;

    const initialAgenda = {
        allow_patient_turn: true,
        days_of_week: [],
        doctor_id: 0,
        from_date: null,
        office_id: 0,
        to_date: null
    };

    const [agenda, setAgenda, setAgendaValue, setAgendaValues] = useObjectState(initialAgenda);
    const { clearError, errors, setErrors } = useErrors();
    const [modalConfirm, setModalConfirm] = useState(false);

    useEffect(() => {
        if (agenda_id) {
            dispatch(turnsActions.setTurnsAgendaError(''));
            dispatch(turnsActions.getAgenda(Number(agenda_id)));
        } else {
            dispatch(turnsActions.setTurnsAgenda({
                ...initialAgenda,
                ...(!!professional && { doctor_id: Number(professional) })
            }));
        }
    }, [agenda_id, professional]);

    useEffect(() => {
        setAgenda(turnsAgenda);
    }, [setAgenda, turnsAgenda]);

    const handleDateChange = (from_date, to_date) => {
        setAgendaValues({
            from_date,
            to_date
        });
        clearError('date');
        dispatch(turnsActions.setTurnsAgendaError(''));
    };

    const handleRangeAdd = (day, from_time, to_time) => {
        const { days_of_week } = agenda;
        setAgendaValue('days_of_week', orderBy([...days_of_week, { day, from_time, to_time }], ['day', 'from_time']));
    };

    const handleRangeRemove = (day, { label }) => {
        const { days_of_week } = agenda;
        const newDays = days_of_week.filter(({
            day: elemDay,
            from_time,
            to_time
        }) => elemDay !== day || label !== `${ from_time } - ${ to_time }`);
        setAgendaValue('days_of_week', newDays);
    };

    const handleRangeValidate = (day, fromTime, toTime) => {
        const { days_of_week } = agenda;
        const ranges = days_of_week.filter(({ day: index }) => index === day);
        return ranges.findIndex(({ from_time, to_time }) => overlapTime(from_time, to_time, fromTime, toTime)) < 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            const data = {
                ...agenda,
                allow_patient_turn: allow_patient_turn ? 1 : 0
            };
            if (createMode) {
                dispatch(turnsActions.createAgenda(data));
            } else if (editMode) {
                dispatch(turnsActions.updateAgenda({ body: data, id: Number(agenda_id) }));
            }
        }
    };

    const onAgendaDelete = action => {
        if (action === 'confirm') {
            dispatch(turnsActions.deleteAgenda(Number(agenda_id)));
        } else {
            setModalConfirm(false);
        }
    };

    const validate = () => {
        const { doctor_id, from_date, office_id, to_date } = agenda;
        const { errors, isValid } = validationSchema({
            date: [!!from_date && !!to_date, t('Requerido')],
            doctor_id: [!!doctor_id || !restricted_access, t('Requerido')],
            office_id: [!!office_id, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const {
        allow_patient_turn,
        days_of_week,
        doctor_id,
        from_date,
        office_id,
        to_date
    } = agenda;
    const days = daysOfWeek.filter(({ index }) => !blockedWeekDays.includes(index));
    const title = createMode ? t('Creando nueva agenda') : editMode ? t('Editando agenda') : t('Viendo agenda');

    return (
        <>
            <FormLayout
                goBackTo={ `${ t('/agenda') }${ doctor_id ? `?professional=${ doctor_id }` : '' }` }
                title={ title }
            >
                <Container>
                    <Row>
                        <Col xs="12">
                            <FormGroup label={ t('Profesional') }>
                                <DoctorPicker
                                    allowNone={ !restricted_access }
                                    hasError={ errors.doctor_id }
                                    nullPlaceholder={ !restricted_access ? t('Agenda por defecto') : undefined }
                                    value={ doctor_id || 0 }
                                    onChange={ value => setAgendaValue('doctor_id', value) }
                                />
                                <ErrorMessage error={ errors.doctor_id } marginLeft={ 0 } marginTop={ 0 } relative={ true } />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup label={ t('Vigencia') }>
                                <DateRangePicker
                                    endValue={ to_date }
                                    startValue={ from_date }
                                    onChange={ handleDateChange }
                                />
                                <ErrorMessage error={ agendaError ? agendaError : errors.date } marginLeft={ 0 } marginTop={ 0 } relative={ true } />
                            </FormGroup>
                        </Col>
                        <Col align="right" md="6">
                            <FormGroup label={ t('Consultorio') }>
                                <OfficePicker
                                    allowNone={ true }
                                    margin="0px"
                                    params={ doctor_id }
                                    value={ office_id ?? 0 }
                                    onChange={ value => setAgendaValue('office_id', value) }
                                />
                                <ErrorMessage error={ errors.office_id } marginTop={ 5 } relative={ true } />
                            </FormGroup>
                        </Col>
                        <Col xs="12">
                            <FormGroup label={ t('Agenda') }>
                                <AgendaContainer>
                                    {
                                        days.map(({ index, label }, idx) => {
                                            const items = days_of_week ? days_of_week.filter(({ day }) => day === index) : [];
                                            return <DayRow key={ idx }>
                                                <DayLabel>
                                                    { label }:
                                                </DayLabel>
                                                <TimeRangePicker
                                                    margin="0px 0px 0px 5px"
                                                    onPick={ (timeFrom, timeTo) => handleRangeAdd(index, timeFrom, timeTo) }
                                                    onValidate={ (timeFrom, timeTo) => handleRangeValidate(index, timeFrom, timeTo) }
                                                />
                                                <TagList
                                                    items={ items.map(({ from_time, to_time }) => ({ label: `${ from_time } - ${ to_time }` })) }
                                                    margin="0px 0px 0px 10px"
                                                    padding="3px 8px"
                                                    readOnly={ false }
                                                    onRemove={ item => handleRangeRemove(index, item) }
                                                />
                                            </DayRow>;
                                        })
                                    }
                                </AgendaContainer>
                            </FormGroup>
                        </Col>
                        <Col xs="12">
                            <FormGroup label={ t('Opciones') }>
                                <Checkbox
                                    checked={ !!allow_patient_turn }
                                    label={ t('Permitir a los pacientes crear turnos') }
                                    onClick={ value => setAgendaValue('allow_patient_turn', value) }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <ButtonsContainer>
                            { !editMode && <ExpandButton caption={ t('Confirmar') } size={ 120 } onClick={ handleSubmit } /> }
                            { editMode && <ExpandButton danger caption={ t('Eliminar') } size={ 120 } onClick={ () => setModalConfirm(true) } /> }
                            { editMode && <ExpandButton caption={ t('Confirmar') } size={ 120 } onClick={ handleSubmit } /> }
                        </ButtonsContainer>
                    </Row>
                </Container>
            </FormLayout>
            <ConfirmationModal
                actionHandler={ onAgendaDelete }
                active={ modalConfirm }
                title={ t('¿Quieres dar de baja esta agenda?') }
            />
        </>
    );
};

export default AgendaRegistration;
